import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Volume2 } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { BUILTIN_SOUNDS, DEFAULT_PREFS, playSound } from "../lib/sounds";
import { inputCls, Panel, btnPrimary, btnSecondary, errMsg } from "./admin/ui";

function SoundSelect({ kind, value, custom, onChange }) {
  const { t } = useI18n();
  return (
    <label className="block">
      <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#D4AF37]">{t(`sound.${kind}`)}</span>
      <div className="flex gap-2 mt-2">
        <select data-testid={`sound-${kind}-select`} value={value} onChange={(e) => onChange(e.target.value)} className={`${inputCls} !mt-0`}>
          <option value="off">{t("sound.off")}</option>
          {BUILTIN_SOUNDS.map((s) => <option key={s} value={s}>{t(`sound.${s}`)}</option>)}
          {custom.length > 0 && <optgroup label={t("sound.custom_group")}>{custom.map((s) => <option key={s.id} value={`custom:${s.id}`}>{s.name}</option>)}</optgroup>}
        </select>
        <button type="button" data-testid={`sound-${kind}-test-btn`} onClick={() => onChange(value, true)} className={`${btnSecondary} flex items-center gap-1`}><Volume2 size={14} />{t("sound.test")}</button>
      </div>
    </label>
  );
}

export default function SoundSettings() {
  const { t } = useI18n();
  const { user, refresh } = useAuth();
  const [prefs, setPrefs] = useState({ ...DEFAULT_PREFS, ...(user.sound_prefs || {}) });
  const [custom, setCustom] = useState([]);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/sounds").then((r) => setCustom(r.data)).catch(() => {}); }, []);

  const change = (kind) => (value, test) => {
    setPrefs({ ...prefs, [kind]: value });
    if (test || value !== prefs[kind]) playSound(value, prefs.volume);
  };
  const save = async () => {
    setSaving(true);
    try { await api.put("/auth/sound-prefs", prefs); await refresh(); toast.success(t("sound.saved")); }
    catch (err) { toast.error(errMsg(err)); } finally { setSaving(false); }
  };

  return (
    <Panel title={t("sound.title")} testId="sound-settings-panel">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <SoundSelect kind="order" value={prefs.order} custom={custom} onChange={change("order")} />
        <SoundSelect kind="message" value={prefs.message} custom={custom} onChange={change("message")} />
      </div>
      <label className="block mt-4">
        <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#D4AF37]">{t("sound.volume")} {Math.round(prefs.volume * 100)}%</span>
        <input data-testid="sound-volume-input" type="range" min="0" max="1" step="0.05" value={prefs.volume}
          onChange={(e) => setPrefs({ ...prefs, volume: Number(e.target.value) })} className="w-full mt-2 accent-[#D4AF37]" />
      </label>
      <p className="text-xs text-[#A8A8AD] mt-2 mb-4">{t("sound.autoplay_note")}</p>
      <button data-testid="sound-save-btn" onClick={save} disabled={saving} className={btnPrimary}>{saving ? t("acct.saving") : t("common.save")}</button>
    </Panel>
  );
}
